import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: {
    name: string;
    avatar: string;
    avatarAlt: string;
  };
  publishDate: string;
  readTime: string;
  image: string;
  imageAlt: string;
  tags: string[];
}

interface BlogCardProps {
  post: BlogPost;
}

const BlogCard = ({ post }: BlogCardProps) => {
  return (
    <article className="group glassmorphic rounded-2xl overflow-hidden shadow-silver-glow hover:shadow-orange-glow-hover transition-all duration-300 card-3d-glow">
      <Link href={`/blog/${post.id}`} className="block">
        <div className="relative h-56 overflow-hidden">
          <AppImage
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary text-primary-foreground shadow-orange-glow">
              {post.category}
            </span>
          </div>
        </div>
        
        <div className="p-6">
          <h3 className="text-xl font-display font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300 line-clamp-2">
            {post.title}
          </h3>
          
          <p className="text-muted-foreground mb-4 line-clamp-3">
            {post.excerpt}
          </p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground"
              >
                #{tag}
              </span>
            ))}
          </div>
          
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center space-x-3">
              <div className="relative w-10 h-10 rounded-full overflow-hidden">
                <AppImage
                  src={post.author.avatar}
                  alt={post.author.avatarAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{post.author.name}</p>
                <p className="text-xs text-muted-foreground">{post.publishDate}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-1 text-muted-foreground">
              <Icon name="ClockIcon" size={16} />
              <span className="text-sm">{post.readTime}</span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default BlogCard;